import apiClient from './api-client';

// ============================================================================
// REQUESTS INTERFACES
// ============================================================================

export interface RightsRequestFilters {
    page?: number;
    pageSize?: number;
    // Support both single and array for backward compatibility
    requestType?: 'CONSENT_WITHDRAWAL' | 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY';
    requestTypes?: Array<'CONSENT_WITHDRAWAL' | 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY'>;
    requestStatus?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
    requestStatuses?: Array<'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED'>;
    priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
    isUrgent?: boolean; // Filter for urgent requests
    assignedTo?: number; // User ID
    startDate?: string; // ISO date string (kept for backward compatibility)
    endDate?: string; // ISO date string (kept for backward compatibility)
    search?: string; // Search (kept for backward compatibility)
    clientId?: number; // Filter by client (for multi-tenant)
}

export interface RightsRequestListItem {
    requestId: number;
    dpId: number;
    clientId: number;
    requestType: string;
    requestStatus: string;
    priority: string;
    description: string;
    reason: string | null;
    purposeId: number | null;
    purposeName: string | null;
    purposeKey: string | null;
    dpIdentifier: string | null; // Email or phone (if test)
    dpIdentifierToken: string | null; // Format-preserving token for production DPs
    dpIdentifierType: string | null;
    assignedTo: number | null;
    assignedToName: string | null;
    estimatedCompletionDate: string | null; // ISO date string
    actualCompletionDate: string | null; // ISO date string
    createdDate: string; // ISO date string
    modifiedDate: string | null; // ISO date string
}

export interface RightsRequestsListData {
    requests: RightsRequestListItem[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface RightsRequestsListResponse {
    success: boolean;
    data: RightsRequestsListData;
    error?: string;
    errorType?: string;
}

export interface RightsRequestDetails extends RightsRequestListItem {
    requestedData: any | null; // JSON parsed
    legalBasis: string | null;
    complianceNotes: string | null;
    createdBy: number | null;
    createdByName: string | null;
    modifiedBy: number | null;
    modifiedByName: string | null;
}

export interface RightsRequestDetailsResponse {
    success: boolean;
    data: RightsRequestDetails;
    error?: string;
    errorType?: string;
}

export interface StatusUpdatePayload {
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
    reason?: string;
    notes?: string; // Processing notes
}

export interface StatusUpdateResponse {
    success: boolean;
    message?: string;
    error?: string;
    errorType?: string;
}

export interface AssignRequestPayload {
    assignedTo: number | null; // null to unassign
}

export interface AssignRequestResponse {
    success: boolean;
    message?: string;
    error?: string;
    errorType?: string;
}

export interface PriorityUpdatePayload {
    priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
}

export interface PriorityUpdateResponse {
    success: boolean;
    message?: string;
    error?: string;
    errorType?: string;
}

export interface AddNotesPayload {
    notes: string; // Compliance notes
}

export interface AddNotesResponse {
    success: boolean;
    message?: string;
    error?: string;
    errorType?: string;
}

// ============================================================================
// SUMMARY INTERFACES
// ============================================================================

export interface RightsRequestsSummary {
    total: number;
    urgent: number; // Priority=URGENT AND deadline within 3 days AND status in (PENDING, IN_PROGRESS)
    open: number; // PENDING + IN_PROGRESS + REJECTED
    closed: number; // COMPLETED + CANCELLED
}

export interface RightsRequestsSummaryResponse {
    success: boolean;
    data: RightsRequestsSummary;
    error?: string;
    errorType?: string;
}

export interface StatusHistoryEntry {
    statusHistoryId: number;
    oldStatus: string | null;
    newStatus: string;
    statusChangeReason: string | null;
    notes: string | null;
    changedDate: string; // ISO date string
    changedBy: number | null;
    changedByName: string | null;
}

export interface StatusHistoryResponse {
    success: boolean;
    data: StatusHistoryEntry[];
    error?: string;
    errorType?: string;
}

// ============================================================================
// REQUESTS SERVICE
// ============================================================================

class RequestsService {
    private readonly basePath = '/admin/requests';

    /**
     * List all rights requests with filtering and pagination
     */
    async listRequests(filters: RightsRequestFilters): Promise<RightsRequestsListResponse> {
        try {
            const queryParams = new URLSearchParams();

            // Add pagination parameters
            if (filters.page !== undefined) {
                queryParams.append('page', filters.page.toString());
            }
            if (filters.pageSize !== undefined) {
                queryParams.append('pageSize', filters.pageSize.toString());
            }

            // Add filter parameters
            // Prefer arrays over single values
            if (filters.requestTypes && filters.requestTypes.length > 0) {
                filters.requestTypes.forEach(type => {
                    queryParams.append('requestTypes', type);
                });
            } else if (filters.requestType) {
                queryParams.append('requestType', filters.requestType);
            }
            
            if (filters.requestStatuses && filters.requestStatuses.length > 0) {
                filters.requestStatuses.forEach(status => {
                    queryParams.append('requestStatuses', status);
                });
            } else if (filters.requestStatus) {
                queryParams.append('requestStatus', filters.requestStatus);
            }
            
            if (filters.isUrgent !== undefined) {
                queryParams.append('isUrgent', filters.isUrgent.toString());
            }
            
            if (filters.priority) {
                queryParams.append('priority', filters.priority);
            }
            
            if (filters.assignedTo !== undefined) {
                queryParams.append('assignedTo', filters.assignedTo.toString());
            }
            // Note: startDate, endDate, search are kept for backward compatibility but not shown in UI
            if (filters.startDate) {
                queryParams.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                queryParams.append('endDate', filters.endDate);
            }
            if (filters.search) {
                queryParams.append('search', filters.search);
            }
            if (filters.clientId !== undefined) {
                queryParams.append('clientId', filters.clientId.toString());
            }

            const queryString = queryParams.toString();
            const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

            const response = await apiClient.get<RightsRequestsListResponse>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch requests');
            }

            return response;
        } catch (error) {
            console.error('Error fetching requests:', error);
            throw error;
        }
    }

    /**
     * Get detailed information for a specific request
     */
    async getRequestDetails(requestId: number): Promise<RightsRequestDetailsResponse> {
        try {
            const response = await apiClient.get<RightsRequestDetailsResponse>(`${this.basePath}/${requestId}`);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch request details');
            }

            return response;
        } catch (error) {
            console.error('Error fetching request details:', error);
            throw error;
        }
    }

    /**
     * Update request status with audit trail
     */
    async updateRequestStatus(
        requestId: number,
        payload: StatusUpdatePayload
    ): Promise<StatusUpdateResponse> {
        try {
            const response = await apiClient.patch<StatusUpdateResponse>(
                `${this.basePath}/${requestId}/status`,
                payload
            );

            if (!response.success) {
                throw new Error(response.error || 'Failed to update request status');
            }

            return response;
        } catch (error) {
            console.error('Error updating request status:', error);
            throw error;
        }
    }

    /**
     * Assign or unassign request to a user
     */
    async assignRequest(requestId: number, payload: AssignRequestPayload): Promise<AssignRequestResponse> {
        try {
            const response = await apiClient.patch<AssignRequestResponse>(
                `${this.basePath}/${requestId}/assign`,
                payload
            );

            if (!response.success) {
                throw new Error(response.error || 'Failed to assign request');
            }

            return response;
        } catch (error) {
            console.error('Error assigning request:', error);
            throw error;
        }
    }

    /**
     * Update request priority
     */
    async updatePriority(requestId: number, payload: PriorityUpdatePayload): Promise<PriorityUpdateResponse> {
        try {
            const response = await apiClient.patch<PriorityUpdateResponse>(
                `${this.basePath}/${requestId}/priority`,
                payload
            );

            if (!response.success) {
                throw new Error(response.error || 'Failed to update request priority');
            }

            return response;
        } catch (error) {
            console.error('Error updating request priority:', error);
            throw error;
        }
    }

    /**
     * Add compliance notes to a request
     */
    async addNotes(requestId: number, payload: AddNotesPayload): Promise<AddNotesResponse> {
        try {
            const response = await apiClient.post<AddNotesResponse>(
                `${this.basePath}/${requestId}/notes`,
                payload
            );

            if (!response.success) {
                throw new Error(response.error || 'Failed to add notes');
            }

            return response;
        } catch (error) {
            console.error('Error adding notes:', error);
            throw error;
        }
    }

    /**
     * Get summary statistics for rights requests
     */
    async getRequestsSummary(): Promise<RightsRequestsSummaryResponse> {
        try {
            const response = await apiClient.get<RightsRequestsSummaryResponse>(`${this.basePath}/summary`);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch requests summary');
            }

            return response;
        } catch (error) {
            console.error('Error fetching requests summary:', error);
            throw error;
        }
    }

    /**
     * Get status history (audit trail) for a specific request
     */
    async getRequestStatusHistory(requestId: number): Promise<StatusHistoryResponse> {
        try {
            const response = await apiClient.get<StatusHistoryResponse>(`${this.basePath}/${requestId}/history`);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch status history');
            }

            return response;
        } catch (error) {
            console.error('Error fetching status history:', error);
            throw error;
        }
    }
}

export const requestsService = new RequestsService();

